import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Returns whether a given page type is indexable according to the SEO module.
 * Only inject JSON-LD schemas when this returns true.
 */
export function useSchemaIndexable(pageType: string, entityId?: string): boolean {
  // Check override first
  const { data: override } = useQuery({
    queryKey: ["seo-override-indexable", pageType, entityId],
    queryFn: async () => {
      let query = supabase
        .from("seo_overrides")
        .select("custom_indexable, custom_robots_directive")
        .eq("page_type", pageType);
      if (entityId) {
        query = query.eq("entity_id", entityId);
      } else {
        query = query.is("entity_id", null);
      }
      const { data } = await query.maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 30,
  });

  // Check template
  const { data: template } = useQuery({
    queryKey: ["seo-template-indexable", pageType],
    queryFn: async () => {
      const { data } = await supabase
        .from("seo_templates")
        .select("indexable, robots_directive")
        .eq("page_type", pageType)
        .eq("is_active", true)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 30,
  });

  // Override takes priority
  if (override?.custom_indexable === false) return false;
  if (override?.custom_robots_directive?.includes("noindex")) return false;
  if (override?.custom_indexable === true) return true;

  // Template
  if (template?.indexable === false) return false;
  if (template?.robots_directive?.includes("noindex")) return false;

  // Default: indexable
  return true;
}
